import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Button,
  Alert,
  alpha,
  useTheme
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { SectionPaper, SectionTitle } from './styledComponents';

const CargoDetailsSection = ({ bol, onUpdate, lineItems, canEdit = true }) => {
  const theme = useTheme();

  // Calculate total quantities from line items
  const calculateQuantities = () => {
    if (!lineItems || lineItems.length === 0) return { cases: 0, units: 0, pallets: 0 };
    
    const totals = lineItems.reduce((acc, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const unitType = item.unit_type || 'Case';
      
      if (unitType === 'Case') acc.cases += qty;
      else if (unitType === 'Unit') acc.units += qty;
      else if (unitType === 'Pallet') acc.pallets += qty;
      
      return acc;
    }, { cases: 0, units: 0, pallets: 0 });

    return totals;
  };

  const quantities = calculateQuantities();

  // Check if manually entered quantities match line items
  const checkQuantityMismatch = () => {
    const manualPallets = parseInt(bol.num_pallets) || 0;
    const manualCases = parseInt(bol.num_cases) || 0;
    const manualUnits = parseInt(bol.num_units) || 0;

    return (
      manualPallets !== quantities.pallets ||
      manualCases !== quantities.cases ||
      manualUnits !== quantities.units
    );
  };

  const hasQuantityMismatch = checkQuantityMismatch() && !!(bol.num_pallets || bol.num_cases || bol.num_units);

  return (
    <SectionPaper sx={{ width: '100%', mt: 3 }}>
      <SectionTitle variant="h6">
        <InventoryIcon />
        Cargo Details
      </SectionTitle>

      <Grid container spacing={3}>
        {/* Description of Goods */}
        <Grid item xs={12}>
          <TextField
            label="Description of Goods"
            fullWidth
            multiline
            rows={4}
            value={bol.goods_description || ''}
            onChange={(e) => canEdit && onUpdate({ goods_description: e.target.value })}
            placeholder="Detailed description of the goods being shipped..."
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        {/* Container Number and Seal Number */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Container Number"
            fullWidth
            value={bol.container_number || ''}
            onChange={(e) => canEdit && onUpdate({ container_number: e.target.value })}
            placeholder="e.g., CSQU3054383"
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Seal Number"
            fullWidth
            value={bol.seal_number || ''}
            onChange={(e) => canEdit && onUpdate({ seal_number: e.target.value })}
            placeholder="e.g., SN123456"
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        {/* Quantity Summary from Line Items */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 2, 
            bgcolor: alpha(theme.palette.info.main, 0.08), 
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: 'info.main' }}>
              Quantities from Proforma Invoice Line Items:
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
              <Typography variant="body2">
                <strong>{quantities.pallets}</strong> Pallet(s)
              </Typography>
              <Typography variant="body2">
                <strong>{quantities.cases}</strong> Case(s)
              </Typography>
              <Typography variant="body2">
                <strong>{quantities.units}</strong> Unit(s)
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                canEdit && onUpdate({
                  num_pallets: quantities.pallets.toString(),
                  num_cases: quantities.cases.toString(),
                  num_units: quantities.units.toString()
                });
              }}
              sx={{ 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.5)}`,
                color: 'info.main',
                fontWeight: 500,
                fontSize: '0.75rem',
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed',
                '&:hover': {
                  border: `1px solid ${theme.palette.info.main}`,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  transform: 'translateY(-1px)',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.info.main, 0.2)}`
                }
              }}
            >
              Auto-fill from Line Items
            </Button>
          </Box>
        </Grid>

        {/* Quantity Mismatch Alert */}
        {hasQuantityMismatch && (
          <Grid item xs={12}>
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                backgroundColor: alpha(theme.palette.warning.main, 0.1)
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Quantity Mismatch Detected
              </Typography>
              <Typography variant="body2">
                The manually entered quantities don't match the Proforma Invoice line items. 
                Expected: <strong>{quantities.pallets} Pallet(s)</strong>, <strong>{quantities.cases} Case(s)</strong>, <strong>{quantities.units} Unit(s)</strong>.
                Current: <strong>{bol.num_pallets || 0} Pallet(s)</strong>, <strong>{bol.num_cases || 0} Case(s)</strong>, <strong>{bol.num_units || 0} Unit(s)</strong>.
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Manual Quantity Entries */}
        <Grid item xs={12} sm={4}>
          <TextField
            label="Number of Pallets"
            type="number"
            fullWidth
            value={bol.num_pallets || ''}
            onChange={(e) => canEdit && onUpdate({ num_pallets: e.target.value })}
            inputProps={{ min: 0, step: 1 }}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Number of Cases"
            type="number"
            fullWidth
            value={bol.num_cases || ''}
            onChange={(e) => canEdit && onUpdate({ num_cases: e.target.value })}
            inputProps={{ min: 0, step: 1 }}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Number of Units"
            type="number"
            fullWidth
            value={bol.num_units || ''}
            onChange={(e) => canEdit && onUpdate({ num_units: e.target.value })}
            inputProps={{ min: 0, step: 1 }}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        {/* Container Dimensions */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
            Container Dimensions
          </Typography>
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label="Length"
            type="number"
            fullWidth
            value={bol.container_length || ''}
            onChange={(e) => canEdit && onUpdate({ container_length: e.target.value })}
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label="Width"
            type="number"
            fullWidth
            value={bol.container_width || ''}
            onChange={(e) => canEdit && onUpdate({ container_width: e.target.value })}
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label="Height"
            type="number"
            fullWidth
            value={bol.container_height || ''}
            onChange={(e) => canEdit && onUpdate({ container_height: e.target.value })}
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Unit</InputLabel>
            <Select
              value={bol.dimension_unit || 'ft'}
              onChange={(e) => canEdit && onUpdate({ dimension_unit: e.target.value })}
              label="Unit"
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed'
              }}
            >
              <MenuItem value="in">Inches</MenuItem>
              <MenuItem value="ft">Feet</MenuItem>
              <MenuItem value="cm">Centimeters</MenuItem>
              <MenuItem value="m">Meters</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Weight */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Total Weight"
            type="number"
            fullWidth
            value={bol.total_weight || ''}
            onChange={(e) => canEdit && onUpdate({ total_weight: e.target.value })}
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Weight Unit</InputLabel>
            <Select
              value={bol.weight_unit || 'lbs'}
              onChange={(e) => canEdit && onUpdate({ weight_unit: e.target.value })}
              label="Weight Unit"
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed'
              }}
            >
              <MenuItem value="lbs">Pounds (lbs)</MenuItem>
              <MenuItem value="kg">Kilograms (kg)</MenuItem>
              <MenuItem value="tons">Tons</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Hazardous Materials */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={bol.hazardous_materials || false}
                onChange={(e) => canEdit && onUpdate({ hazardous_materials: e.target.checked })}
                sx={{
                  color: theme.palette.warning.main,
                  opacity: canEdit ? 1 : 0.5,
                  pointerEvents: canEdit ? 'auto' : 'none',
                  cursor: canEdit ? 'pointer' : 'not-allowed',
                  '&.Mui-checked': {
                    color: theme.palette.warning.main,
                  }
                }}
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Hazardous Materials
              </Typography>
            }
          />
        </Grid>

        {bol.hazardous_materials && (
          <>
            <Grid item xs={12}>
              <TextField
                label="Hazardous Material Details"
                fullWidth
                multiline
                rows={3}
                value={bol.hazmat_details || ''}
                onChange={(e) => canEdit && onUpdate({ hazmat_details: e.target.value })}
                placeholder="UN Number, Class, Packing Group, etc..."
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                    background: alpha(theme.palette.warning.main, 0.05),
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="UN Number"
                fullWidth
                value={bol.un_number || ''}
                onChange={(e) => canEdit && onUpdate({ un_number: e.target.value })}
                placeholder="e.g., UN1203"
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                    background: alpha(theme.palette.warning.main, 0.05),
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Hazard Class"
                fullWidth
                value={bol.hazard_class || ''}
                onChange={(e) => canEdit && onUpdate({ hazard_class: e.target.value })}
                placeholder="e.g., Class 3"
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                    background: alpha(theme.palette.warning.main, 0.05),
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
          </>
        )}

        {/* Special Handling Instructions */}
        <Grid item xs={12}>
          <TextField
            label="Special Handling Instructions"
            fullWidth
            multiline
            rows={3}
            value={bol.special_handling || ''}
            onChange={(e) => canEdit && onUpdate({ special_handling: e.target.value })}
            placeholder="Temperature control, fragile items, special equipment needed, etc..."
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>
      </Grid>
    </SectionPaper>
  );
};

export default CargoDetailsSection;